import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { QueryHistoryEntry, QueryHistoryContextKey } from '../types/queryHistoryTypes';
import { createHistoryContextKey, createHistoryEntryId } from '../types/queryHistoryTypes';
import { createGuardedJsonStorage } from '../../../core/storage/guardedJsonStorage';
import { showPersistWriteFailureToast } from '../../../components/ui/persistWriteFailureToast';
import { exceedsPersistedSqlLimit } from '../../../types/editorTextLimits';

/** Maximum number of history entries to keep per connection/database context */
const MAX_ENTRIES_PER_CONTEXT = 200;

/** Number of recent entries to show in the overlay */
export const RECENT_HISTORY_COUNT = 5;

/** Persist-time cap (see editorTextLimits.ts); same reference when nothing changes. */
export function capHistoryForPersistence(
  historyByContext: Record<QueryHistoryContextKey, QueryHistoryEntry[]>,
): Record<QueryHistoryContextKey, QueryHistoryEntry[]> {
  let changed = false;
  const result: Record<QueryHistoryContextKey, QueryHistoryEntry[]> = {};
  for (const [contextKey, entries] of Object.entries(historyByContext)) {
    const kept = entries.filter((entry) => !exceedsPersistedSqlLimit(entry.sql));
    if (kept.length !== entries.length) changed = true;
    result[contextKey] = kept.length !== entries.length ? kept : entries;
  }
  return changed ? result : historyByContext;
}

/** Stable empty array to prevent infinite re-renders in selectors */
const EMPTY_ENTRIES: QueryHistoryEntry[] = [];

type QueryHistoryState = {
  /** History entries keyed by context (connectionId:database) */
  historyByContext: Record<QueryHistoryContextKey, QueryHistoryEntry[]>;

  /**
   * Add a query to history
   */
  addEntry: (entry: Omit<QueryHistoryEntry, 'id'>) => void;

  /**
   * Get history entries for a specific connection/database context
   */
  getEntriesForContext: (connectionId: string, database?: string) => QueryHistoryEntry[];

  /**
   * Get the most recent N entries for a connection/database context
   */
  getRecentEntries: (connectionId: string, database?: string, count?: number) => QueryHistoryEntry[];

  /**
   * Clear history for a specific context
   */
  clearContext: (connectionId: string, database?: string) => void;

  /**
   * Clear all history
   */
  clearAll: () => void;
};

export const useQueryHistoryStore = create<QueryHistoryState>()(
  persist(
    (set, get) => ({
      historyByContext: {},

      addEntry: (entryData) => {
        const contextKey = createHistoryContextKey(entryData.connectionId, entryData.database);
        const entry: QueryHistoryEntry = {
          ...entryData,
          id: createHistoryEntryId(),
        };

        set((state) => {
          const existingEntries = state.historyByContext[contextKey] || [];

          // Check for duplicate (same SQL executed recently)
          // Skip if the exact same query was just executed (within last entry)
          if (existingEntries.length > 0) {
            const lastEntry = existingEntries[0];
            if (lastEntry.sql.trim() === entry.sql.trim()) {
              // Update the timestamp and stats of the existing entry instead
              const updatedEntries = [
                { ...lastEntry, ...entry, id: lastEntry.id },
                ...existingEntries.slice(1),
              ];
              return {
                historyByContext: {
                  ...state.historyByContext,
                  [contextKey]: updatedEntries.slice(0, MAX_ENTRIES_PER_CONTEXT),
                },
              };
            }
          }

          // Add new entry at the beginning (most recent first)
          const newEntries = [entry, ...existingEntries].slice(0, MAX_ENTRIES_PER_CONTEXT);

          return {
            historyByContext: {
              ...state.historyByContext,
              [contextKey]: newEntries,
            },
          };
        });
      },

      getEntriesForContext: (connectionId, database) => {
        const contextKey = createHistoryContextKey(connectionId, database);
        return get().historyByContext[contextKey] || EMPTY_ENTRIES;
      },

      getRecentEntries: (connectionId, database, count = RECENT_HISTORY_COUNT) => {
        const entries = get().getEntriesForContext(connectionId, database);
        return entries.slice(0, count);
      },

      clearContext: (connectionId, database) => {
        const contextKey = createHistoryContextKey(connectionId, database);
        set((state) => {
          const newHistory = { ...state.historyByContext };
          delete newHistory[contextKey];
          return { historyByContext: newHistory };
        });
      },

      clearAll: () => {
        set({ historyByContext: {} });
      },
    }),
    {
      name: 'sql-studio-query-history',
      storage: createGuardedJsonStorage({ onFailure: showPersistWriteFailureToast('query history') }),
      // Only persist the history data. Entries whose SQL exceeds the
      // persisted-SQL cap are left out of the snapshot (kept in memory for
      // this run) — a multi-MB body would push the store past the quota.
      partialize: (state) => ({ historyByContext: capHistoryForPersistence(state.historyByContext) }),
      // Convert Date strings back to Date objects on rehydration
      onRehydrateStorage: () => (state) => {
        if (state?.historyByContext) {
          for (const contextKey of Object.keys(state.historyByContext)) {
            state.historyByContext[contextKey] = state.historyByContext[contextKey].map(
              (entry) => ({
                ...entry,
                executedAt: new Date(entry.executedAt),
              })
            );
          }
        }
      },
    }
  )
);
